import { IconButton } from '@mui/material';
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { FaCalendarAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { ApprovalFlowStatus } from '@/constants/ApprovalConstants';
import { transportTypeTranslations } from '@/constants/TransportTypeOptions';
import { ExtraTripSolicitationDto } from '@/types/extra-trip-solicitation.types';
import { TransportType } from '@/types/TransportType';
import {
	getApprovalStatusLabel,
	getDayStatusLabel,
} from '@/utils/extraTripsApprovals';
import ExtraTripCardMenu from './ExtraTripCardMenu';
import ExtraTripDayMenu from './ExtraTripDayMenu';

type ExtraTripCardProps = ExtraTripSolicitationDto & {
	onEdit?: (id: string | number) => void;
	onCancel?: (id: string | number) => Promise<void>;
	onCancelDay?: (
		solicitationId: string | number,
		dayId: string,
	) => Promise<void>;
	isHistoryView?: boolean;
	defaultExpanded?: boolean;
	onExpand?: () => void;
	onCollapse?: () => void;
};

export type ExtraTripCardRef = {
	scrollIntoView: () => void;
};

function getTrasnportType(day: string | undefined) {
	return transportTypeTranslations[day as TransportType] || '-';
}

const ExtraTripCard = forwardRef<ExtraTripCardRef, ExtraTripCardProps>(
	(
		{
			id,
			category,
			reason,
			requester,
			approver,
			approvalDate,
			coveredEmployee,
			detail,
			management,
			teamStructure,
			team,
			vacancy,
			days = [],
			approvalStatus,
			onEdit,
			onCancel,
			onCancelDay,
			isHistoryView = false,
			defaultExpanded = false,
			onExpand,
			onCollapse,
		},
		ref,
	) => {
		const [isExpanded, setIsExpanded] = useState(defaultExpanded);
		const approvalStatusInfo = getApprovalStatusLabel(approvalStatus);
		const cardRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			if (defaultExpanded) {
				setIsExpanded(true);
			}
		}, [defaultExpanded]);

		const handleToggle = () => {
			const wasExpanded = isExpanded;
			setIsExpanded(!isExpanded);

			if (!wasExpanded && onExpand) {
				onExpand();
			}
			if (wasExpanded && onCollapse) {
				onCollapse();
			}
		};

		useImperativeHandle(ref, () => ({
			scrollIntoView: () => {
				if (cardRef.current) {
					cardRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					});
				}
			},
		}));

		return (
			<div
				ref={cardRef}
				className="rounded-md border border-[#CBD5E1] px-[20px] py-[14px]"
			>
				<div className="relative flex w-full items-start">
					<button
						type="button"
						aria-expanded={isExpanded}
						aria-controls={`extra-trip-details-${id}`}
						aria-label="Expandir ou recolher detalhes da viagem extra"
						onClick={handleToggle}
						className="flex-1 text-left focus:outline-none rounded pr-38"
					>
						<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-[9rem_7.5rem_minmax(0,1fr)_minmax(0,1fr)_10rem] gap-4">
							<div className="flex flex-col items-start">
								<span className="font-graphie text-[12px] font-bold text-[#193CB9]">
									Motivo
								</span>
								<span className="font-graphie text-[12px] font-normal text-[#505050]">
									{reason}
								</span>
							</div>
							<div className="flex flex-col items-start">
								<span className="font-graphie text-[12px] font-bold text-[#193CB9]">
									Categoria
								</span>
								<span className="font-graphie text-[12px] font-normal text-[#505050]">
									{category}
								</span>
							</div>
							<div className="flex flex-col items-start">
								<span className="font-graphie text-[12px] font-bold text-[#193CB9]">
									Solicitante
								</span>
								<span className="font-graphie text-[12px] font-normal text-[#505050]">
									{requester}
								</span>
							</div>
							<div className="flex flex-col items-start">
								<span className="font-graphie text-[12px] font-bold text-[#193CB9]">
									Aprovador
								</span>
								<span className="font-graphie text-[12px] font-normal text-[#505050]">
									{approver}
								</span>
							</div>
							<div className="flex flex-col items-start">
								<span className="font-graphie text-[12px] font-bold text-[#193CB9]">
									Data de aprovação
								</span>
								<span className="font-graphie text-[12px] font-normal text-[#505050]">
									{approvalDate}
								</span>
							</div>
						</div>
					</button>
					<div className="absolute right-0 top-0 flex items-center gap-2">
						<span
							className={`rounded-[4px] px-[8px] py-1 font-graphie text-[10px] font-normal ${approvalStatusInfo.bgColor} ${approvalStatusInfo.textColor} pointer-events-none`}
							aria-hidden="true"
						>
							{approvalStatusInfo.label}
						</span>
						<span
							role="none"
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
							className="inline-block"
						>
							<ExtraTripCardMenu
								onEdit={() => onEdit?.(id)}
								onCancel={() => onCancel?.(id) ?? Promise.resolve()}
								isHistoryView={isHistoryView}
								approvalStatus={approvalStatus as ApprovalFlowStatus}
							/>
						</span>
						<IconButton
							aria-label="Expandir ou recolher detalhes da viagem extra"
							onClick={handleToggle}
						>
							{isExpanded ? (
								<FaChevronUp className="text-primary" size={16} />
							) : (
								<FaChevronDown className="text-primary" size={16} />
							)}
						</IconButton>
					</div>
				</div>

				{isExpanded && (
					<div id={`extra-trip-details-${id}`} className="pt-[10px]">
						<div className="mb-[10px] rounded-md border border-[#CBD5E1] p-3">
							<div className="mb-4 grid grid-cols-4 gap-4">
								<div className="flex flex-col items-start">
									<span
										className="font-graphie text-[12px] text-[#505050]"
										style={{ fontWeight: 600 }}
									>
										Detalhamento
									</span>
									<span className="font-graphie text-[12px] font-normal text-[#505050]">
										{detail}
									</span>
								</div>
								<div className="flex flex-col items-start">
									<span
										className="font-graphie text-[12px] text-[#505050]"
										style={{ fontWeight: 600 }}
									>
										Gerência
									</span>
									<span className="font-graphie text-[12px] font-normal text-[#505050]">
										{management}
									</span>
								</div>
								<div className="flex flex-col items-start">
									<span
										className="font-graphie text-[12px] text-[#505050]"
										style={{ fontWeight: 600 }}
									>
										Estrutura
									</span>
									<span className="font-graphie text-[12px] font-normal text-[#505050]">
										{teamStructure}
									</span>
								</div>
								<div className="flex flex-col items-start">
									<span className="font-graphie text-[12px] font-semibold text-[#505050]">
										Turma
									</span>
									<span className="font-graphie text-[12px] font-normal text-[#505050]">
										{team !== '-' ? team : '-'}
									</span>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div className="flex flex-col items-start">
									<span
										className="font-graphie text-[12px] text-[#505050]"
										style={{ fontWeight: 600 }}
									>
										Colaborador Coberto
									</span>
									<span className="font-graphie text-[12px] font-normal text-[#505050]">
										{coveredEmployee}
									</span>
								</div>
								<div className="flex flex-col items-start">
									<span
										className="font-graphie text-[12px] text-[#505050]"
										style={{ fontWeight: 600 }}
									>
										Vaga
									</span>
									<span className="font-graphie text-[12px] font-normal text-[#505050]">
										{vacancy}
									</span>
								</div>
							</div>
						</div>

						{days.length > 0 && (
							<div className="space-y-[10px]">
								{days.map((day) => {
									const statusInfo = getDayStatusLabel(day.status);
									return (
										<div
											key={day.id}
											className="relative rounded-md border border-[#CBD5E1] p-3"
										>
											<div className="mb-3 flex items-center gap-3 pr-8">
												<div className="flex items-center gap-2">
													<FaCalendarAlt className="text-[#505050]" />
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{day.date}
													</span>
												</div>
												<span
													className={`rounded-[4px] px-[8px] py-1 font-graphie text-[10px] font-normal ${statusInfo.bgColor} ${statusInfo.textColor}`}
												>
													{statusInfo.label}
												</span>
											</div>
											{approvalStatus === ApprovalFlowStatus.ACCEPTED && (
												<div className="absolute right-1 top-1">
													<ExtraTripDayMenu
														approvalStatus={approvalStatus}
														dayDate={day.date}
														dayStatus={day.status}
														onCancel={
															onCancelDay
																? () => onCancelDay(id, day.id)
																: undefined
														}
													/>
												</div>
											)}
											<div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-4">
												<div className="flex flex-col items-start">
													<span
														className="font-graphie text-[12px] text-[#505050]"
														style={{ fontWeight: 600 }}
													>
														Horário de entrada
													</span>
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{day.entryTime || '-'}
													</span>
												</div>
												<div className="flex flex-col items-start">
													<span
														className="font-graphie text-[12px] text-[#505050]"
														style={{ fontWeight: 600 }}
													>
														Horário de saída
													</span>
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{day.exitTime || '-'}
													</span>
												</div>
												<div className="flex flex-col items-start">
													<span
														className="font-graphie text-[12px] text-[#505050]"
														style={{ fontWeight: 600 }}
													>
														Local de trabalho
													</span>
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{day.workStation || '-'}
													</span>
												</div>
												<div className="flex flex-col items-start">
													<span
														className="font-graphie text-[12px] text-[#505050]"
														style={{ fontWeight: 600 }}
													>
														Confinamento
													</span>
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{day.confinement || '-'}
													</span>
												</div>
												<div className="flex flex-col items-start">
													<span
														className="font-graphie text-[12px] text-[#505050]"
														style={{ fontWeight: 600 }}
													>
														Tipo de necessidade de transporte
													</span>
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{getTrasnportType(day.transportType)}
													</span>
												</div>
												<div className="flex flex-col items-start">
													<span
														className="font-graphie text-[12px] text-[#505050]"
														style={{ fontWeight: 600 }}
													>
														Status do chamado de transporte
													</span>
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{day.requestiaStatus || '-'}
													</span>
												</div>
												<div className="flex flex-col items-start">
													<span
														className="font-graphie text-[12px] text-[#505050]"
														style={{ fontWeight: 600 }}
													>
														Código do requestia
													</span>
													<span className="font-graphie text-[12px] font-normal text-[#505050]">
														{day.requestiaId || '-'}
													</span>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				)}
			</div>
		);
	},
);

ExtraTripCard.displayName = 'ExtraTripCard';

export default ExtraTripCard;
